import { useEffect, useState } from 'react'

import { Loader, TextInput } from '@mantine/core'
import { CircleAlert, Search } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import CarrierOptionBody from 'src/components/Inputs/CarrierAutocomplete/CarrierOptionBody'
import {
  MIN_QUERY_LENGTH,
  type CarrierSearchState,
  type FmcsaSearchCompany,
} from 'src/components/Inputs/CarrierAutocomplete/useCarrierSearch'
import {
  PickList,
  pickListOptionId,
  usePickListKeyboard,
} from 'src/components/Inputs/PickList/PickList'
import {
  FormAddressField,
  FormCompanyDotField,
  FormTextField,
} from 'src/components/Overlays/EmploymentDetailsDrawer/components/FormComponents'

import type { EmployerMode } from './addEmploymentFlow'
import type { EmploymentForm } from './formValues'
import { SectionLink, SectionTitle, StageActions } from './stageParts'

const RESULTS_ID = 'add-employment-carrier-results'
/** A search still running after this long stops holding the recruiter up. */
export const SLOW_REGISTRY_MS = 6000
// One empty list, so the keyboard highlight survives re-renders while waiting
const NO_RESULTS: FmcsaSearchCompany[] = []

export interface EmployerStageProps {
  mode: EmployerMode
  /** The employer is settled and a later stage is showing; render it read-only. */
  collapsed: boolean
  form: EmploymentForm
  query: string
  /** The form's company-name error: a Save was attempted with no employer settled. */
  queryError?: React.ReactNode
  onQueryChange: (query: string) => void
  search: CarrierSearchState
  onPick: (company: FmcsaSearchCompany, rank: number) => void
  /** `registryAnswered` is false when the recruiter typed past a search still running. */
  onAddAsTyped: (params: { registryAnswered: boolean }) => void
  /** A DOT typed into the DOT row found a carrier: the registry's facts take over. */
  onDotLookupPick: (company: FmcsaSearchCompany) => void
  onChange: () => void
  onSearchInstead: () => void
  onContinue: () => void
  /** The section is folded to its title line. */
  folded?: boolean
  onToggleFold?: () => void
}

/** Shown once the registry has answered the current text: results, none, or an error. */
export const hasRegistryAnswered = (
  query: string,
  search: CarrierSearchState
) =>
  query.trim().length >= MIN_QUERY_LENGTH &&
  search.searched &&
  !search.searching

const EmployerSearch = ({
  query,
  queryError,
  onQueryChange,
  search,
  onPick,
  onAddAsTyped,
}: Pick<
  EmployerStageProps,
  | 'query'
  | 'queryError'
  | 'onQueryChange'
  | 'search'
  | 'onPick'
  | 'onAddAsTyped'
>) => {
  const typed = query.trim()
  const longEnough = typed.length >= MIN_QUERY_LENGTH
  const settled = hasRegistryAnswered(query, search)
  // Typing that has not settled yet counts as in flight: the hook only says
  // "searching" once the debounced text is long enough to have run.
  const inFlight = longEnough && (search.searching || !search.searched)
  const items = inFlight ? NO_RESULTS : search.results

  // A registry that has gone quiet should not hold the recruiter hostage
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    setSlow(false)
    if (!inFlight) return undefined
    const timer = setTimeout(() => setSlow(true), SLOW_REGISTRY_MS)
    return () => clearTimeout(timer)
  }, [inFlight, typed])

  const offerTyped = settled || (inFlight && slow)
  const trailing = offerTyped
    ? {
        caption: 'None of these',
        label: `Add "${typed}" as typed`,
        onPick: () => onAddAsTyped({ registryAnswered: settled }),
      }
    : null
  const keyboard = usePickListKeyboard({
    items,
    trailing,
    onPick: (company, index) => onPick(company, index + 1),
  })

  const status = inFlight ? (
    slow ? (
      <span className="flex flex-row items-center justify-center gap-2">
        <Loader size={14} color="gray" />
        The carrier registry is taking a while
      </span>
    ) : (
      <Loader size={18} color="gray" />
    )
  ) : !settled ? null : search.failed ? (
    <span className="flex flex-row items-center justify-center gap-2 text-doubleNickel-warning-500">
      <CircleAlert size={14} className="stroke-doubleNickel-warning-500" />
      Autocomplete is unavailable
    </span>
  ) : search.throttled ? (
    'The carrier registry is busy, try again in a moment'
  ) : search.results.length === 0 ? (
    'No suggestions found'
  ) : null

  return (
    <div className="space-y-1">
      <TextInput
        size="xs"
        value={query}
        onChange={(event) => onQueryChange(event.currentTarget.value)}
        onKeyDown={keyboard.onKeyDown}
        placeholder="Company name or DOT number"
        error={queryError}
        leftSection={<Search size={14} />}
        aria-label="Company name or DOT number"
        role="combobox"
        aria-expanded={longEnough}
        aria-controls={RESULTS_ID}
        aria-autocomplete="list"
        aria-activedescendant={
          keyboard.highlighted === null
            ? undefined
            : pickListOptionId(RESULTS_ID, keyboard.highlighted)
        }
        autoComplete="off"
        data-autofocus
        classNames={{ input: 'text-xs' }}
      />
      {longEnough && (
        <PickList
          id={RESULTS_ID}
          aria-label="Carriers"
          items={items}
          itemKey={(company, index) =>
            `${company.dotNumber}-${company.companyName}-${index}`
          }
          renderItem={(company) => <CarrierOptionBody company={company} />}
          onPick={(company, index) => onPick(company, index + 1)}
          trailing={trailing}
          status={status}
          highlighted={keyboard.highlighted}
          onHighlight={keyboard.setHighlighted}
        />
      )}
    </div>
  )
}

const EmployerStage = ({
  mode,
  collapsed,
  form,
  query,
  queryError,
  onQueryChange,
  search,
  onPick,
  onAddAsTyped,
  onDotLookupPick,
  onChange,
  onSearchInstead,
  onContinue,
  folded = false,
  onToggleFold,
}: EmployerStageProps) => {
  const editing = mode === 'typed' && !collapsed

  return (
    <div className="space-y-3">
      <SectionTitle
        folded={folded}
        onToggle={onToggleFold}
        action={
          mode === 'search' ? undefined : collapsed || mode === 'picked' ? (
            <SectionLink onClick={onChange}>Change</SectionLink>
          ) : (
            <SectionLink onClick={onSearchInstead}>Search instead</SectionLink>
          )
        }
      >
        Company information
      </SectionTitle>

      {folded ? null : mode === 'search' ? (
        <EmployerSearch
          query={query}
          queryError={queryError}
          onQueryChange={onQueryChange}
          search={search}
          onPick={onPick}
          onAddAsTyped={onAddAsTyped}
        />
      ) : (
        <>
          <FormTextField
            label="Company Name"
            required={editing}
            editing={editing}
            formProps={{
              ...form.getInputProps('companyName'),
              placeholder: 'Enter company name',
            }}
          />
          <FormCompanyDotField
            label="DOT Number"
            editing={editing}
            formProps={{
              ...form.getInputProps('dotNumber'),
              placeholder: 'Enter DOT number',
            }}
            onSelect={(company) => company && onDotLookupPick(company)}
          />
          <FormAddressField
            label="Address"
            editing={editing}
            formProps={{ ...form.getInputProps('address') }}
          />
          {editing && (
            <StageActions>
              <DNButton variant="primary" onClick={onContinue}>
                Continue
              </DNButton>
            </StageActions>
          )}
        </>
      )}
    </div>
  )
}

export default EmployerStage
